import React, { useMemo, useRef } from 'react';
import { useTheme } from '@material-ui/core';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { getMax } from '../../../utils/helper';
import './style.css';

interface IDimensioningChartProps {
  title?: string;
  showTitle?: boolean;
  labels: string[];
  historical: number[];
  executed: number[];
  ideal: number[];
  year: number;
}

const DimensioningChart: React.FC<IDimensioningChartProps> = ({
  labels,
  historical,
  executed,
  ideal,
  year,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const chartRef = useRef<any>(null);

  const yMax = useMemo(() => {
    const rawMax = getMax([getMax(executed), getMax(historical)]);
    return Math.ceil(rawMax / 10) * 10;
  }, [executed, historical]);

  const excess = useMemo(() => {
    const res = [];
    for (let i = 0; i < executed.length; i += 1) {
      res.push(yMax);
    }
    return res;
  }, [executed, yMax]);

  const rawData = useMemo(() => {
    return [
      {
        name: t('screens:dimensioning.ideal'),
        type: 'area',
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      },
      {
        name: t('screens:dimensioning.excess'),
        type: 'area',
        data: excess,
      },
      {
        name: t('screens:dimensioning.scarcity'),
        type: 'area',
        data: ideal,
      },
      {
        name: `${t('screens:dimensioning.historic')} ${year - 1}`,
        type: 'column',
        data: historical,
      },
      {
        name: t('screens:dimensioning.executed'),
        type: 'column',
        data: executed,
      },
    ];
  }, [excess, executed, historical, ideal, year, t]);

  const transformedData = useMemo(() => {
    const transformed = [];
    for (let i = 0; i < rawData.length; i += 1) {
      const newDt = rawData[i].data.map((dt, idx) => ({
        x: labels[idx].toUpperCase(),
        y: dt,
      }));
      const res = {
        name: rawData[i].name,
        type: rawData[i].type,
        data: newDt,
      };
      transformed.push(res);
    }
    return transformed;
  }, [labels, rawData]);

  const markers = useMemo(() => {
    const mks: any = [];
    if (ideal) {
      ideal.forEach((sel, idx) =>
        mks.push({
          x: labels[idx].toUpperCase(),
          y: sel,
          marker: {
            shape: 'square',
            fillColor: '#FFF500',
            strokeWidth: 0,
            offsetX: -10,
            cssClass: 'annotation',
          },
        }),
      );
    }
    return mks;
  }, [ideal, labels]);

  return (
    <Chart
      series={transformedData}
      options={{
        chart: {
          fontFamily: 'Poppins',
          toolbar: {
            show: false,
          },
        },
        plotOptions: {
          bar: {
            columnWidth: '48px',
          },
          area: {
            fillTo: 'origin',
          },
        },
        dataLabels: {
          enabled: false,
        },
        annotations: {
          points: markers,
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent'],
        },
        grid: {
          show: false,
        },
        xaxis: {
          tickPlacement: 'on',
          type: 'category',
          axisTicks: {
            show: false,
          },
        },
        yaxis: {
          max: yMax,
          min: 0,
          tickAmount: 4,
          axisTicks: {
            show: true,
          },
          axisBorder: {
            show: true,
          },
        },
        colors: [
          '#D7C7E3',
          '#C8DEEC',
          theme.palette.primary.main,
          theme.palette.secondary.main,
        ],
        fill: {
          type: 'solid',
          colors: [
            '#FFF500',
            '#D7C7E3',
            '#C8DEEC',
            theme.palette.primary.main,
            theme.palette.secondary.main,
          ],
        },
        labels: [
          t('screens:dimensioning.ideal'),
          t('screens:dimensioning.excess'),
          t('screens:dimensioning.scarcity'),
          `${t('screens:dimensioning.historic')} ${year - 1}`,
          t('screens:dimensioning.executed'),
        ],
        legend: {
          showForNullSeries: true,
          showForSingleSeries: true,
          show: true,
          markers: {
            fillColors: [
              '#FFF500',
              '#D7C7E3',
              '#C8DEEC',
              theme.palette.primary.main,
              theme.palette.secondary.main,
            ],
          },
        },
      }}
      type="bar"
      width="100%"
      height={200}
      ref={chartRef}
    />
  );
};

export default DimensioningChart;
